import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Users, Award } from "lucide-react";
import { API } from "../App";
import axios from "axios";

export default function LiveDisplayPage() {
  const { competitionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [competitionId]);

  const fetchLiveData = async () => {
    try {
      const response = await axios.get(`${API}/live/${competitionId}`);
      setData(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching live data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { competition, festival, current_heat, next_heat, top_leaders, top_followers } = data || {};

  return (
    <div className="min-h-screen bg-black text-white projector-mode overflow-hidden">
      {/* Header */}
      <header className="py-[2vh] px-[3vw] flex items-center justify-between border-b-2 border-[#FFD700]">
        <div className="flex items-center gap-[1.5vw]">
          <Trophy className="w-[4vw] h-[4vw] text-[#FFD700]" />
          <div>
            <h1 className="font-['Playfair_Display'] text-[3vw] font-bold">{festival?.name}</h1>
            <p className="text-[1.5vw] text-[#A1A1AA]">{competition?.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-[1vw]">
          {competition?.status === "active" && (
            <div className="flex items-center gap-[0.5vw] px-[1.5vw] py-[0.5vh] bg-[#FF007F]/20 rounded-full">
              <span className="w-[1vw] h-[1vw] bg-[#FF007F] rounded-full animate-pulse"></span>
              <span className="text-[#FF007F] text-[1.5vw] font-bold uppercase">Live</span>
            </div>
          )}
          <span className="text-[1.5vw] text-[#A1A1AA]">
            Round {competition?.current_round || 1} of {competition?.rounds?.length || 3}
          </span>
        </div>
      </header>

      <main className="flex h-[calc(100vh-10vh)]">
        {/* Left Side - Current Heat */}
        <div className="w-[50%] p-[3vw] flex flex-col">
          <AnimatePresence mode="wait">
            {current_heat ? (
              <motion.div
                key={current_heat.heat_id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex-1 flex flex-col justify-center"
              >
                <div className="text-center mb-[4vh]">
                  <span className="text-[2vw] text-[#A1A1AA] uppercase tracking-widest">Now Dancing</span>
                  <div className="flex items-center justify-center gap-[2vw] mt-[1vh]">
                    <span className="text-[#FFD700] text-[4vw] font-bold">Heat</span>
                    <span className="font-['Playfair_Display'] text-[8vw] font-bold text-[#FFD700]">
                      {current_heat.heat_number}
                    </span>
                  </div>
                </div>

                <div className="glass p-[3vw] rounded-[2vw]">
                  <div className="grid grid-cols-2 gap-[4vw]">
                    {/* Leader */}
                    <div className="text-center">
                      <div className="w-[2vw] h-[2vw] rounded-full bg-[#FFD700] mx-auto mb-[1vh]"></div>
                      <span className="text-[1.5vw] text-[#A1A1AA] uppercase">Leader</span>
                      <h2 className="font-['Playfair_Display'] text-[4vw] font-bold mt-[0.5vh]">
                        {current_heat.leader_name}
                      </h2>
                    </div>
                    {/* Follower */}
                    <div className="text-center">
                      <div className="w-[2vw] h-[2vw] rounded-full bg-[#FF007F] mx-auto mb-[1vh]"></div>
                      <span className="text-[1.5vw] text-[#A1A1AA] uppercase">Follower</span>
                      <h2 className="font-['Playfair_Display'] text-[4vw] font-bold mt-[0.5vh]">
                        {current_heat.follower_name}
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Next Heat Preview */}
                {next_heat && (
                  <div className="mt-[4vh] text-center opacity-60">
                    <span className="text-[1.5vw] text-[#A1A1AA] uppercase">Up Next</span>
                    <p className="text-[2vw] mt-[0.5vh]">
                      {next_heat.leader_name} & {next_heat.follower_name}
                    </p>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex-1 flex items-center justify-center"
              >
                <div className="text-center">
                  <Users className="w-[8vw] h-[8vw] text-[#27272a] mx-auto mb-[2vh]" />
                  <p className="text-[2.5vw] text-[#A1A1AA]">Waiting for next heat...</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Side - Leaderboard */}
        <div className="w-[50%] bg-[#0a0a0a] p-[3vw] border-l border-white/10 overflow-hidden">
          <div className="flex items-center gap-[1vw] mb-[3vh]">
            <Award className="w-[3vw] h-[3vw] text-[#FFD700]" />
            <h2 className="font-['Playfair_Display'] text-[3vw] font-bold">Leaderboard</h2>
          </div>

          <div className="grid grid-cols-2 gap-[2vw] h-[calc(100%-8vh)]">
            {/* Leaders */}
            <div>
              <div className="flex items-center gap-[0.5vw] mb-[2vh]">
                <div className="w-[1vw] h-[1vw] rounded-full bg-[#FFD700]"></div>
                <span className="text-[1.5vw] text-[#A1A1AA] uppercase">Leaders</span>
              </div>
              <div className="space-y-[1.5vh]">
                {top_leaders?.map((dancer, index) => (
                  <motion.div
                    key={dancer.dancer_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center p-[1vw] rounded-lg ${
                      index === 0 ? 'bg-gradient-to-r from-[#FFD700]/20 to-transparent border-l-4 border-[#FFD700]' :
                      index === 1 ? 'bg-gradient-to-r from-[#C0C0C0]/10 to-transparent border-l-4 border-[#C0C0C0]' :
                      index === 2 ? 'bg-gradient-to-r from-[#CD7F32]/10 to-transparent border-l-4 border-[#CD7F32]' :
                      'bg-white/5'
                    }`}
                    data-testid={`live-leader-${dancer.dancer_id}`}
                  >
                    <span className={`font-['Playfair_Display'] text-[2.5vw] font-bold w-[3vw] ${
                      index < 3 ? 'text-[#FFD700]' : 'text-[#52525B]'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 ml-[1vw]">
                      <p className="text-[1.8vw] font-medium truncate">{dancer.name}</p>
                    </div>
                    <span className="text-[2vw] font-bold text-[#FFD700]">
                      {(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(1)}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Followers */}
            <div>
              <div className="flex items-center gap-[0.5vw] mb-[2vh]">
                <div className="w-[1vw] h-[1vw] rounded-full bg-[#FF007F]"></div>
                <span className="text-[1.5vw] text-[#A1A1AA] uppercase">Followers</span>
              </div>
              <div className="space-y-[1.5vh]">
                {top_followers?.map((dancer, index) => (
                  <motion.div
                    key={dancer.dancer_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center p-[1vw] rounded-lg ${
                      index === 0 ? 'bg-gradient-to-r from-[#FFD700]/20 to-transparent border-l-4 border-[#FFD700]' :
                      index === 1 ? 'bg-gradient-to-r from-[#C0C0C0]/10 to-transparent border-l-4 border-[#C0C0C0]' :
                      index === 2 ? 'bg-gradient-to-r from-[#CD7F32]/10 to-transparent border-l-4 border-[#CD7F32]' :
                      'bg-white/5'
                    }`}
                    data-testid={`live-follower-${dancer.dancer_id}`}
                  >
                    <span className={`font-['Playfair_Display'] text-[2.5vw] font-bold w-[3vw] ${
                      index < 3 ? 'text-[#FFD700]' : 'text-[#52525B]'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 ml-[1vw]">
                      <p className="text-[1.8vw] font-medium truncate">{dancer.name}</p>
                    </div>
                    <span className="text-[2vw] font-bold text-[#FFD700]">
                      {(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(1)}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
